// In-house i18n for the Inertia surfaces. Rails owns locale *selection*
// (shared as the `locale` prop) and translation *content* (config/locales,
// compiled to JSON by `bin/rails i18n:export`). This module is just the
// client-side reader: nested-key lookup, %{var} interpolation, one/other
// pluralization, and the nn → nb → en fallback chain that mirrors
// config.i18n.fallbacks. No runtime i18n dependency by design.
import { usePage } from '@inertiajs/react'
import nb from '@/locales/nb.json'
import nn from '@/locales/nn.json'
import en from '@/locales/en.json'
import type { TKey } from '@/locales/keys'

export type Locale = 'nb' | 'nn' | 'en'
export type Vars = Record<string, string | number>

export const LOCALES: Locale[] = ['nb', 'nn', 'en']
export const DEFAULT_LOCALE: Locale = 'nb'

// Active locale → BCP-47 tag for Intl date/number/relative-time formatting.
// Both written Norwegian standards format with Bokmål data (there is no widely
// supported nn-NO CLDR locale); English uses en-GB (day-first, 24h). Single
// source of truth so date/time formatting stays consistent across surfaces.
const INTL_TAG: Record<Locale, string> = {
  nb: 'nb-NO',
  nn: 'nb-NO',
  en: 'en-GB',
}

export function intlTag(locale: Locale): string {
  return INTL_TAG[locale]
}

type Bundle = Record<string, unknown>
const BUNDLES: Record<Locale, Bundle> = { nb, nn, en }

// Mirrors the Rails fallback map (config.i18n.fallbacks).
const FALLBACKS: Record<Locale, Locale[]> = {
  nb: ['nb', 'en'],
  nn: ['nn', 'nb', 'en'],
  en: ['en', 'nb'],
}

function lookup(bundle: Bundle, key: string): unknown {
  return key.split('.').reduce<unknown>((node, segment) => {
    if (node && typeof node === 'object' && segment in node) {
      return (node as Record<string, unknown>)[segment]
    }
    return undefined
  }, bundle)
}

function resolveRaw(locale: Locale, key: string): unknown {
  for (const candidate of FALLBACKS[locale] ?? [DEFAULT_LOCALE]) {
    const bundle = BUNDLES[candidate]
    if (!bundle) continue
    const found = lookup(bundle, key)
    if (found !== undefined) return found
  }
  return undefined
}

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template
  return template.replace(/%\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  )
}

function pluralForm(forms: Record<string, unknown>, count: number): string | undefined {
  const form = count === 1 ? forms.one : forms.other
  return typeof form === 'string' ? form : undefined
}

// Pure translation — exported for unit tests and non-component callers.
export function translate(locale: Locale, key: TKey, vars?: Vars): string {
  const raw = resolveRaw(locale, key as string)

  if (typeof raw === 'string') return interpolate(raw, vars)

  if (raw && typeof raw === 'object' && typeof vars?.count === 'number') {
    const picked = pluralForm(raw as Record<string, unknown>, vars.count)
    if (picked !== undefined) return interpolate(picked, vars)
  }

  if (import.meta.env?.DEV) console.warn(`[i18n] missing key "${key}" (${locale})`)
  return key as string
}

// Active locale from the Inertia shared prop, validated against LOCALES.
export function useLocale(): Locale {
  const { locale } = usePage().props as { locale?: string }
  return LOCALES.includes(locale as Locale) ? (locale as Locale) : DEFAULT_LOCALE
}

// Bound t() for components: const t = useT(); t('greeting', { name }).
export function useT(): (key: TKey, vars?: Vars) => string {
  const locale = useLocale()
  return (key, vars) => translate(locale, key, vars)
}
